import React, { useEffect, useMemo, useRef, useState } from 'react';

import Header from 'components/header.js';
import Footer from 'components/footer.js';
import Container from 'react-bootstrap/Container';
import { CheckAuth } from 'pages/auth/CheckAuth';
import { Row } from 'react-table';
import { MdPlaylistAddCheckCircle } from 'react-icons/md';
import {
	actionFetchYardWeightFileList,
	actionArchiveYardWeightFile,
	YardWeightFile,
	YardWeightRecord,
	actionCommitYardWeightFile,
} from './actions';
import Loading from 'components/loading';
import { format } from 'date-fns';
import {
	Alert,
	Button,
	CloseButton,
	Overlay,
	Popover,
	PopoverBody,
	PopoverHeader,
	Table,
} from 'react-bootstrap';
import OPWBreadcrumb from 'components/breadcrumb';
import { TableControl } from 'components/tableControl';

const BC = OPWBreadcrumb;

/**
 * YardWeights - a help and information screen for the manual entering of yard weights
 */

const TITLE = 'Yard Weights';

export const YardWeights = () => {
	useEffect(() => {
		document.title = TITLE;
	}, []);
	//const cols = {lhs: 3, rhs: 9}
	return (
		<Template>
			<div className="yardWeightsList">
				<YardWeightsContent />
			</div>
		</Template>
	);
};

const Template: React.FC<{ children: React.ReactNode }> = (props) => (
	<Container fluid>
		<CheckAuth groupName={'superuser'} />
		<Header>
			<BC trail={`[["Home", "/"], ["Staff", "/staff"], ["${TITLE}", ""]]`} />
		</Header>
		{props.children}
		<Footer />
	</Container>
);

type FileRow = { value: string; row: Row<YardWeightFile> };

export const YardWeightsContent = () => {
	const [data, setData] = useState<YardWeightFile[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined
	);
	const [happyMessage, setHappyMessage] = useState<string | undefined>(
		undefined
	);

	const removeFileFromList = (fileKey: string) => {
		console.log(`Removing file ${fileKey} from table`);
		setData((prevData) => prevData.filter((row) => row.key !== fileKey));
	};

	const handleArchive = (fileInfo: YardWeightFile) => {
		console.log(`Archiving file with ID: ${fileInfo.key}`);
		if (!fileInfo.key) return;
		setIsLoading(true);
		actionArchiveYardWeightFile(fileInfo)
			.then(() => {
				console.log(`File ${fileInfo.key} archived successfully`);
				setHappyMessage(`File ${fileInfo.key} archived successfully`);
				removeFileFromList(fileInfo.key!);
			})
			.catch((error) => {
				setErrorMessage(error.message);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleCommit = (fileInfo: YardWeightFile) => {
		console.log(`Committing file with ID: ${fileInfo.key}`);
		setIsLoading(true);
		actionCommitYardWeightFile(fileInfo)
			.then(() => {
				console.log(`File ${fileInfo.key} committed successfully`);
				setHappyMessage(`File ${fileInfo.key} committed successfully`);
				removeFileFromList(fileInfo.key!);
			})
			.catch((error: Error) => {
				setErrorMessage(error.message);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const columns = useMemo(
		() => [
			{
				Header: 'Client',
				accessor: 'clientName',
			},
			{
				Header: 'ClientId',
				accessor: 'clientId',
			},
			{
				Header: 'Session',
				accessor: 'sessionId',
			},
			{
				Header: 'Uploaded By',
				accessor: 'sessionEmail',
			},
			{
				Header: 'File Name',
				accessor: 'key',
				Cell: ({ value, row }: FileRow) => {
					return <a href={row.original.downloadUrl?.toString()}>{value}</a>;
				},
			},
			{
				Header: 'Parsed records',
				accessor: 'parseSummary.records',
				Cell: ({ value, row }: FileRow) => {
					const [show, setShow] = useState(false);
					const [target, setTarget] = useState<HTMLElement | null>(null);
					const ref = useRef(null);

					const handleClick = (event: React.MouseEvent<HTMLElement>) => {
						setShow(!show);
						setTarget(event.target as HTMLElement);
					};

					return (
						<div ref={ref}>
							<span className="clickable" onClick={handleClick}>
								{value || undefined}
							</span>
							<Overlay
								show={show}
								target={target}
								container={ref}
								placement="left"
								transition={true}
								rootClose={true}
							>
								{parsedRecordsTooltip(
									row.original,
									() => {
										handleCommit(row.original);
										setShow(false);
									},
									() => setShow(false)
								)}
							</Overlay>
						</div>
					);
				},
			},
			{
				Header: 'Size (kB)',
				accessor: 'size',
				Cell: ({ value }: FileRow) => (Number(value) / 1024).toFixed(2),
			},
			{
				Header: 'Modified Date',
				accessor: 'lastModified',
				Cell: ({ value }: FileRow) => new Date(value).toLocaleDateString(),
			},
			{
				Header: 'Actions',
				id: 'actions',
				Cell: ({ row }: FileRow) => (
					<button
						onClick={() => handleArchive(row.original)}
						className="btn btn-link p-1"
						title="Archive file"
					>
						<MdPlaylistAddCheckCircle
							className="text-secondary"
							style={{ width: '2rem', height: '2rem' }}
						/>
					</button>
				),
			},
		],
		[]
	);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await actionFetchYardWeightFileList();
				setData(response);
			} catch (error) {
				setErrorMessage((error as Error).message);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, []);

	const parsedRecordsTooltip = (
		row: YardWeightFile,
		commitData: () => void,
		closeHandler: () => void
	) => {
		const { parseSummary, parsePreview } = row;

		const formatDate = (date: Date) => {
			return format(new Date(date), 'dd-MMM-yyyy HH:mm:ss XXX');
		};

		const renderParsedRecordsTable = (records?: YardWeightRecord[]) => {
			if (!records) return null;

			const uniqueKeys = Array.from(
				new Set(records.flatMap((record) => Object.keys(record)))
			).sort();

			return (
				<Table striped bordered hover size="sm">
					<thead>
						<tr>
							{uniqueKeys.map((key) => (
								<th key={key}>{key}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{records.map(
							(
								record: { [key: string]: number | Date | string | undefined },
								index
							) => (
								<tr key={index}>
									{uniqueKeys.map((key) => (
										<td key={key}>
											{record[key] instanceof Date
												? formatDate(record[key])
												: record[key]}
										</td>
									))}
								</tr>
							)
						)}
					</tbody>
				</Table>
			);
		};

		return (
			<Popover style={{ maxWidth: 'unset' }}>
				<PopoverHeader className="d-grid">
					<Button size="sm" onClick={commitData} className="load-to-db">
						Commit to DB
					</Button>
					<span className="header-title">Parsing results</span>
					<CloseButton onClick={closeHandler} className="close-button" />
				</PopoverHeader>
				<PopoverBody>
					<div>
						<b>Records</b>: {parseSummary?.records}
					</div>
					<div>
						<b>Earliest</b>:{' '}
						{parseSummary?.startDate ? formatDate(parseSummary.startDate) : ''}
					</div>
					<div>
						<b>Latest</b>:{' '}
						{parseSummary?.endDate ? formatDate(parseSummary.endDate) : ''}
					</div>
					<hr />
					<h5>First 3 parsed records:</h5>
					{renderParsedRecordsTable(parsePreview?.first3)}
					<h5>Last 3 parsed records:</h5>
					{renderParsedRecordsTable(parsePreview?.last3)}
				</PopoverBody>
			</Popover>
		);
	};

	return (
		<div className={`table-responsive ${isLoading ? 'loading' : ''}`}>
			{errorMessage && (
				<Alert
					variant={'danger'}
					dismissible={true}
					key={errorMessage}
					onClose={() => setErrorMessage(undefined)}
				>
					{errorMessage}
				</Alert>
			)}
			{happyMessage && (
				<Alert
					variant={'primary'}
					dismissible={true}
					key={happyMessage}
					onClose={() => setHappyMessage(undefined)}
				>
					{happyMessage}
				</Alert>
			)}
			<Loading loading={isLoading} />
			<TableControl
				columns={columns}
				data={data}
				showRecCount={50}
				handleCellUpdate={() => {}}
				tableClasses="table table-striped table-hover table-bordered"
				includeTotals={false}
			/>
		</div>
	);
};
